<template>
  <div v-if="selectedOffice" class="mt-6">
    <div class="w-full mb-6 flex flex-wrap -mx-2">
      <div class="w-full md:w-1/4 px-2">
        <input type="text" v-model="searchParams.q" placeholder="Search" class="w-full rounded bg-white p-3" />
      </div>

      <div class="w-full md:w-1/4 px-2">
        <select v-model="searchParams.type_id" class="w-full rounded bg-white p-3">
          <option :value="null">[All Types]</option>
          <option :value="1">Vendor</option>
          <option :value="2">Applicant</option>
        </select>
      </div>

      <div class="w-full md:w-1/4 px-2">
        <select v-model="searchParams.general_status_id" class="w-full rounded bg-white p-3">
          <option :value="null">[All Statuses]</option>
          <option value="2">New</option>
          <option value="5,9">In Progress</option>
          <option value="3">Completed</option>
          <option value="4,6,7">Closed</option>
        </select>
      </div>

      <div class="w-full md:w-1/4 px-2 flex items-center">
        <div class="uppercase inline-block mr-3 md:mr-5 lg:mr-5 xl:mr-5 text-xs">
          <input type="radio" id="active" v-model="searchParams.active" value="1">
          <label for="active">Active</label>
        </div>

        <div class="uppercase inline-block mr-3 md:mr-5 lg:mr-5 xl:mr-5 text-xs">
          <input type="radio" id="inactive" v-model="searchParams.active" value="0">
          <label for="inactive">Inactive</label>
        </div>
      </div>
    </div>

    <loader v-if="referrals === null" />

    <panel v-else-if="referrals.data.length === 0" class="mt-5 pt-6">
      <strong class="mb-2">No Referrals</strong>
      <p>Your search returned no results</p>
    </panel>

    <panel class="mb-6 pt-6" v-else>
      <div class="w-full max-w-full overflow-x-auto overflow-y-hidden px-1">
        <table class="table-auto text-center w-full">
          <thead class="uppercase text-xs">
            <tr>
              <th class="text-left p-2 whitespace-no-wrap cursor-pointer">
                ID
              </th>
              <th class="text-left p-2 whitespace-no-wrap cursor-pointer">
                Referral
              </th>
              <th class="text-left p-2 whitespace-no-wrap cursor-pointer">
                Status
              </th>
              <th class="text-left p-2 whitespace-no-wrap cursor-pointer">
                {{ direction === 'received' ? 'Received From' : 'Sent To' }}
              </th>
              <th class="text-left p-2 whitespace-no-wrap cursor-pointer">
                {{ direction === 'received' ? 'Received At' : 'Sent At' }}
              </th>
              <th class="text-left p-2 whitespace-no-wrap cursor-pointer">
                Updated At
              </th>
            </tr>
          </thead>
          <tbody class="text-sm">
            <tr v-for="(referral, i) in referrals.data" :key="i"
                class="border-t border-grey-lighter cursor-pointer"
                :class="direction === 'received' && !referral.accepted_at && !referral.cancelled_at && ![4, 15].includes(referral.status_id) ? 'bg-green-lightest hover:bg-green-lighter' : referral.status_id === 15 ? 'bg-yellow-lighter' : 'hover:bg-grey-lightest'"
                @click="$emit('selected', referral.id)">
              <td class="text-left p-2">
                <strong class="block mb-1">{{ referral.id }}</strong>
                {{ referral.type_id === 1 ? 'Vendor' : 'Applicant' }}
              </td>
              <td class="text-left p-2">
                <strong class="block mb-1 uppercase" v-if="direction === 'received' && !referral.accepted_at">{{ referral.cancelled_at ? 'Cancelled' : 'New' }} {{ referral.type_id === 1 ? 'vendor' : 'applicant' }} referral</strong>
                <strong class="block mb-1" v-else>{{ referral.vendor_title }} {{ referral.vendor_first_name }} {{ referral.vendor_surname }}</strong>
                <span v-if="direction === 'received' && !referral.accepted_at">
                  {{ referral.type_id === 1 ? referral.address_thoroughfare + ' ' + referral.address_line3 + ' ' + referral.address_postcode : referral.desired_area }}
                  <br>
                  {{ referral.vendor_phone_restricted }}
                </span>
                <span v-else>
                  {{ [referral.address_name_number, referral.address_thoroughfare, referral.address_line2, referral.address_line3, referral.address_town, referral.address_line4, referral.address_postcode].filter(Boolean).join(', ') }}
                </span>
              </td>
              <td class="text-left p-2">
                <strong class="block mb-1">{{ getGeneralStatusName(referral.general_status_id) }}</strong>
                {{ getStatusName(referral.status_id) }}
              </td>
              <td class="text-left p-2">
                <div v-if="direction === 'received' && referral.referred_by_office">
                  <strong class="block mb-1">{{ referral.referred_by_office.name }}</strong>
                  <span class="block">{{ referral.referred_by_office.address_town }}</span>
                  <span v-if="referral.referral_fee_override" class="inline-block text-xs rounded-full whitespace-no-wrap bg-yellow-lighter px-3 py-1 mt-1">
                    <i class="fas fa-coins"></i> Custom Fee Agreement
                  </span>
                </div>
                <div v-else-if="direction === 'sent' && referral.referred_to_office">
                  <strong class="block mb-1">{{ referral.referred_to_office.name }}</strong>
                  {{ referral.referred_to_office.address_town }}
                </div>
                <span v-else>[Awaiting Assignment]</span>
              </td>
              <td class="text-left p-2">
                <div v-if="referral.sent_at">
                  <strong class="block mb-1">{{ referral.sent_at | date }}</strong>
                  {{ referral.sent_at | time }}
                </div>
              </td>
              <td class="text-left p-2">
                <div v-if="referral.status_updated_at || referral.sent_at">
                  <strong class="block mb-1">{{ referral.status_updated_at ? referral.status_updated_at : referral.sent_at | date }}</strong>
                  {{ referral.status_updated_at ? referral.status_updated_at : referral.sent_at | time }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <paginator :data="referrals" @change="pageChanged"/>
    </panel>
  </div>
</template>

<script>
import { v2Api } from '@/http'
import { mapActions, mapState } from 'vuex'
import { debounce } from 'lodash'
import Loader from '../../components/Loader.vue'
export default {
  components: { Loader },
  props: {
    direction: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      referrals: null,
      searchParams: {
        type_id: null,
        general_status_id: null,
        q: null,
        active: '1',
        page: 1
      }
    }
  },
  computed: {
    ...mapState(['selectedOffice'])
  },
  created () {
    this.search()
  },
  methods: {
    ...mapActions(['setPageLoading']),
    search: debounce(function () {
      this.setPageLoading(true)
      v2Api.get('referrals', {
        params: this.direction === 'received' ? {
          referred_to_office_id: this.selectedOffice.id,
          ...this.searchParams
        } : (this.selectedOffice.lead_source_id ? {
          lead_source_id: this.selectedOffice.lead_source_id,
          network_id: this.selectedOffice.network_id,
          ...this.searchParams
        } : {
          referred_by_office_id: this.selectedOffice.id,
          ...this.searchParams
        })
      }).then(response => {
        this.referrals = response.data
        this.setPageLoading(false)
      })
    }, 500),
    getGeneralStatusName (id) {
      switch (id) {
        case 0: return 'New'
        case 1: return 'Needs Assigning'
        case 2: return 'Needs Accepting'
        case 3: return 'Completed'
        case 4: return 'Declined'
        case 5: return 'In Progress'
        case 6: return 'Closed'
        case 7: return 'Cancelled'
        case 8: return 'Cancellation Requested'
        case 9: return 'Finalising'
      }

      return null
    },
    getStatusName (id) {
      switch (id) {
        case 1: return 'Created'
        case 2: return this.direction === 'sent' ? 'Sent' : 'Received'
        case 3: return 'Accepted'
        case 4: return 'Rejected'
        case 5: return 'Valuation'
        case 6: return 'Listed'
        case 7: return 'SSTC'
        case 8: return 'Exchanged'
        case 9: return 'Completed'
        case 11: return 'Complete'
        case 12: return 'Cancelled'
        case 13: return 'New Lead'
        case 14: return 'Closed'
        case 15: return 'Commission Negotiation'
      }

      return null
    },
    pageChanged (page) {
      this.searchParams.page = page
    }
  },
  watch: {
    selectedOffice: {
      handler () {
        this.search()
      },
      deep: true
    },
    searchParams: {
      handler () {
        this.search()
      },
      deep: true
    }
  }
}
</script>
